import React, {useEffect, useState, Fragment} from "react";
import * as Sentry from '@sentry/browser';
import Api from "../../Api";
import {withAuth} from "../../hoc/Auth";
import {Column, Container, Wrapper} from "../../hoc/Layout";
import {useParams} from "react-router-dom";
import Loader from "../../Components/Utils/Loader";
import Amount from "../../Components/Utils/Amount";
import Icon from "../../Components/Utils/Icon";
import VTabBar from "../../Components/Utils/VTabBar";
import TabBarItem from "../../Components/Utils/VTabBar/TabBarItem";
import {FormattedMessage} from "react-intl";

import classes from "./styles.module.css";

function formatBalance(value) {
    if (value === null || value === undefined) return "0,00";
    return Number(value).toLocaleString("ru-RU", {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

/** Строка терминала с раскрытием комиссий */
/**
 * Суммирует балансы терминалов проекта по режимам.
 *
 * Приём и выплаты складываются раздельно — они несовместимы по смыслу.
 *
 * Главное: деньги общего счёта провайдера входят в сумму ОДИН раз. У части
 * провайдеров счёт один на точку входа, а терминалов у нас на неё несколько:
 * каждый показывает остаток целиком и показывает верно, но сложение придумывает
 * деньги, которых нет. Бэкенд помечает такие строки полем sharedBalanceGroup —
 * одинаковое значение означает одни и те же деньги. 10.09.2026 боевой кабинет
 * PAG показал «доступно для выплат 1 190 354,51 ₽» вместо 796 555,36 ₽.
 *
 * Общие у связок ТОЛЬКО деньги. Оборот и «в обработке» у каждой свои,
 * настоящие, и складываются как обычно: первая версия правки пропускала у
 * повторной связки все три числа и потеряла оборот целого терминала.
 *
 * Из группы берётся самый осторожный остаток: наименьший остаток счёта минус
 * все резервы группы. Остатки связок одного счёта на секунды расходятся, и
 * завышать «доступно для выплат» нельзя — мерчант на эти деньги рассчитывает.
 */
export function sumSections(projectBalances) {
    var sections = {
        payin: {transactionBalance: 0, settlementBalance: 0, actualBalance: 0, count: 0, labels: null, hints: null},
        payout: {transactionBalance: 0, settlementBalance: 0, actualBalance: 0, count: 0, labels: null, hints: null}
    };
    var groups = {};

    for (var i = 0; i < projectBalances.length; i++) {
        var b = projectBalances[i];
        var key = b.mode === 'payout' || b.isCrm ? 'payout' : 'payin';
        var turnover = b.transactionBalance || 0;
        var reserve = b.settlementBalance || 0;
        var actual = b.actualBalance || 0;

        // Собственные числа связки — всегда как есть.
        sections[key].transactionBalance += turnover;
        sections[key].settlementBalance += reserve;

        var group = b.sharedBalanceGroup ? key + '|' + b.sharedBalanceGroup : null;
        if (group === null) {
            sections[key].actualBalance += actual;
        } else {
            var raw = actual + reserve;
            if (!groups[group]) {
                groups[group] = {raw: raw, reserve: reserve, counted: actual};
                sections[key].actualBalance += actual;
            } else {
                var g = groups[group];
                g.raw = Math.min(g.raw, raw);
                g.reserve += reserve;
                var groupActual = g.raw - g.reserve;
                sections[key].actualBalance += (groupActual - g.counted);
                g.counted = groupActual;
            }
        }

        // Терминал в счётчике учитывается всегда: он у мерчанта есть,
        // даже если его деньги уже посчитаны соседней строкой.
        sections[key].count++;
        if (b.labels && !sections[key].labels) sections[key].labels = b.labels;
        if (b.hints && !sections[key].hints) sections[key].hints = b.hints;
    }

    return sections;
}

/**
 * Администратор может открыть любой проект, хотя /merchant/balances возвращает
 * только проекты, явно привязанные к его аккаунту. В таком случае используем
 * те же рассчитанные поля из /merchant/{uuid}, а не показываем нули и оборот
 * выплаты вместо доступного остатка.
 */
export function projectTerminalsToBalances(terminals) {
    return (terminals || []).map(function (terminal) {
        return {
            terminal_id: terminal.terminalId,
            terminal_name: terminal.terminalName || terminal.name,
            mode: terminal.mode,
            isCrm: terminal.isCrm,
            labels: terminal.labels,
            hints: terminal.hints,
            transactionBalance: Math.round(Number(terminal.transactionBalance || 0) * 100),
            settlementBalance: Math.round(Number(terminal.settlementBalance || 0) * 100),
            actualBalance: Math.round(Number(terminal.actualBalance || 0) * 100),
            transactionBalanceFloat: Number(terminal.transactionBalance || 0),
            settlementBalanceFloat: Number(terminal.settlementBalance || 0),
            actualBalanceFloat: Number(terminal.actualBalance || 0),
            sharedBalanceGroup: terminal.sharedBalanceGroup || null
        };
    });
}

function TerminalRow({data, balanceData, sharedWith}) {
    var _s = useState(false);
    var isOpened = _s[0];
    var setIsOpened = _s[1];

    var hasPaymentFee = data.fee && data.fee.payment;
    var hasPayoutFee = data.fee && data.fee.payout;
    var hasFees = hasPaymentFee || hasPayoutFee;

    return (
        React.createElement(Fragment, null,
            React.createElement("tr", {
                className: classes.TableRow,
                onClick: function () { if (hasFees || balanceData) setIsOpened(!isOpened); }
            },
                React.createElement("td", null, data.name + (data.terminalId ? " (ID: " + data.terminalId + ")" : "")),
                React.createElement("td", null,
                    data.enabled
                        ? React.createElement("span", {className: classes.Enabled}, "Активен")
                        : React.createElement("span", {className: classes.Disabled}, "Заблокирован")
                ),
                React.createElement("td", null,
                    balanceData
                        ? React.createElement(Fragment, null,
                            formatBalance(balanceData.actualBalanceFloat) + " \u20BD",
                            // У части провайдеров счёт один на несколько наших
                            // терминалов: они показывают ОДНИ И ТЕ ЖЕ деньги.
                            // Без подписи это выглядит как удвоенный остаток, а
                            // в итог такая сумма входит один раз.
                            sharedWith && sharedWith.length > 0
                                ? React.createElement("span", {
                                    className: classes.SharedBalanceNote,
                                    title: "Общий счёт провайдера с терминалом " +
                                        (sharedWith.length > 1 ? "ами " : "") + sharedWith.join(", ") +
                                        ". Это одни и те же деньги, в итог они входят один раз."
                                }, " общий счёт")
                                : null
                        )
                        : (data.balance && data.balance.confirmed !== undefined
                            ? React.createElement(Amount, {amount: data.balance.confirmed, currency: data.currency})
                            : "\u2014")
                ),
                React.createElement("td", null, data.currency || "\u2014"),
                React.createElement("td", null,
                    (hasFees || balanceData)
                        ? React.createElement(Icon, {
                            className: classes.ChevronIcon,
                            icon: isOpened ? "cheveron-up" : "cheveron-down",
                            size: "18px"
                        })
                        : null
                )
            ),
            isOpened ? React.createElement("tr", null,
                React.createElement("td", {colSpan: 5, style: {padding: 0}},
                    // Три баланса
                    balanceData ? React.createElement("div", {className: classes.TerminalBalanceDetail},
                        React.createElement("div", {className: classes.TerminalBalanceItem},
                            React.createElement("span", {className: classes.TerminalBalanceItemLabel, title: balanceData.hints ? balanceData.hints.transactionBalance : ""},
                                balanceData.labels ? balanceData.labels.transactionBalance : "Оборот",
                                balanceData.hints ? React.createElement("span", {style: {cursor: "help", opacity: 0.5, marginLeft: 4}}, "ℹ") : null
                            ),
                            React.createElement("span", {
                                className: classes.TerminalBalanceItemValue,
                                style: {color: "#35C98E"}
                            }, formatBalance(balanceData.transactionBalanceFloat) + " \u20BD")
                        ),
                        React.createElement("div", {className: classes.TerminalBalanceItem},
                            React.createElement("span", {className: classes.TerminalBalanceItemLabel, title: balanceData.hints ? balanceData.hints.settlementBalance : ""},
                                balanceData.labels ? balanceData.labels.settlementBalance : "Выведено",
                                balanceData.hints ? React.createElement("span", {style: {cursor: "help", opacity: 0.5, marginLeft: 4}}, "ℹ") : null
                            ),
                            React.createElement("span", {
                                className: classes.TerminalBalanceItemValue,
                                style: {color: "#2479E0"}
                            }, formatBalance(balanceData.settlementBalanceFloat) + " \u20BD")
                        ),
                        React.createElement("div", {className: classes.TerminalBalanceItem},
                            React.createElement("span", {className: classes.TerminalBalanceItemLabel, title: balanceData.hints ? balanceData.hints.actualBalance : ""},
                                balanceData.labels ? balanceData.labels.actualBalance : "Доступно к выводу",
                                balanceData.hints ? React.createElement("span", {style: {cursor: "help", opacity: 0.5, marginLeft: 4}}, "ℹ") : null
                            ),
                            React.createElement("span", {
                                className: classes.TerminalBalanceItemValue,
                                style: {color: "#E8A317"}
                            }, formatBalance(balanceData.actualBalanceFloat) + " \u20BD")
                        )
                    ) : null,
                    // Комиссии
                    hasFees ? React.createElement("div", {style: {padding: "12px 20px"}},
                        React.createElement(VTabBar, null,
                            hasPaymentFee ? React.createElement(TabBarItem, {
                                label: "Платежи",
                                navIcon: "percent",
                                navIconSize: 16,
                                isVisible: true
                            },
                                React.createElement("table", {className: classes.FeeTable},
                                    React.createElement("tbody", null,
                                        React.createElement("tr", null,
                                            React.createElement("td", null,
                                                React.createElement(FormattedMessage, {
                                                    id: "TerminalsTable.fee.fixed",
                                                    defaultMessage: "Комиссия за операцию:"
                                                })
                                            ),
                                            React.createElement("td", null,
                                                React.createElement(FormattedMessage, {
                                                    id: "TerminalsTable.fee.fixedValue",
                                                    defaultMessage: "фикс. {value}",
                                                    values: {value: data.fee.payment.fixedPerTransaction}
                                                })
                                            )
                                        ),
                                        React.createElement("tr", null,
                                            React.createElement("td", null,
                                                React.createElement(FormattedMessage, {
                                                    id: "TerminalsTable.fee.percent",
                                                    defaultMessage: "Процент:"
                                                })
                                            ),
                                            React.createElement("td", null,
                                                React.createElement(FormattedMessage, {
                                                    id: "TerminalsTable.fee.percentValue",
                                                    defaultMessage: "{percent}% (минимум {min}\u0440)",
                                                    values: {
                                                        percent: data.fee.payment.cost,
                                                        min: data.fee.payment.costFixedMinimum
                                                    }
                                                })
                                            )
                                        )
                                    )
                                )
                            ) : null,
                            hasPayoutFee ? React.createElement(TabBarItem, {
                                label: "Выплаты",
                                navIcon: "wallet",
                                navIconSize: 16,
                                isVisible: true
                            },
                                React.createElement("table", {className: classes.FeeTable},
                                    React.createElement("tbody", null,
                                        React.createElement("tr", null,
                                            React.createElement("td", null,
                                                React.createElement(FormattedMessage, {
                                                    id: "TerminalsTable.fee.fixed",
                                                    defaultMessage: "Комиссия за операцию:"
                                                })
                                            ),
                                            React.createElement("td", null,
                                                React.createElement(FormattedMessage, {
                                                    id: "TerminalsTable.fee.fixedValue",
                                                    defaultMessage: "фикс. {value}",
                                                    values: {value: data.fee.payout.fixedPerTransaction}
                                                })
                                            )
                                        ),
                                        React.createElement("tr", null,
                                            React.createElement("td", null,
                                                React.createElement(FormattedMessage, {
                                                    id: "TerminalsTable.fee.percent",
                                                    defaultMessage: "Процент:"
                                                })
                                            ),
                                            React.createElement("td", null,
                                                React.createElement(FormattedMessage, {
                                                    id: "TerminalsTable.fee.percentValue",
                                                    defaultMessage: "{percent}% (минимум {min}\u0440)",
                                                    values: {
                                                        percent: data.fee.payout.cost,
                                                        min: data.fee.payout.costFixedMinimum
                                                    }
                                                })
                                            )
                                        )
                                    )
                                )
                            ) : null
                        )
                    ) : null
                )
            ) : null
        )
    );
}

const ProjectSettings = withAuth(function (props) {
    var token = props.token;
    var uuid = useParams().uuid;

    var _project = useState(null);
    var project = _project[0];
    var setProject = _project[1];

    var _balances = useState(null);
    var balances = _balances[0];
    var setBalances = _balances[1];

    var _loading = useState(true);
    var isLoading = _loading[0];
    var setIsLoading = _loading[1];

    useEffect(function () {
        setIsLoading(true);
        var api = new Api({token: token});

        Promise.all([
            api.getProjectData(uuid),
            api.getMerchantBalances()
        ]).then(function (results) {
            setProject(results[0]);

            // Фильтруем балансы только по этому проекту
            var allBalances = results[1];
            if (allBalances && allBalances.balances) {
                var projectBalances = allBalances.balances.filter(function (b) {
                    return b.merchant_uuid === uuid;
                });

                if (projectBalances.length === 0 && results[0] && results[0].terminals) {
                    projectBalances = projectTerminalsToBalances(results[0].terminals);
                }

                var sections = sumSections(projectBalances);
                function buildSection(s) {
                    if (s.count === 0) return null;
                    return {
                        count: s.count,
                        transactionBalance: s.transactionBalance,
                        settlementBalance: s.settlementBalance,
                        actualBalance: s.actualBalance,
                        transactionBalanceFloat: Math.round((s.transactionBalance / 100) * 100) / 100,
                        settlementBalanceFloat: Math.round((s.settlementBalance / 100) * 100) / 100,
                        actualBalanceFloat: Math.round((s.actualBalance / 100) * 100) / 100,
                        labels: s.labels,
                        hints: s.hints
                    };
                }
                var payinT = buildSection(sections.payin);
                var payoutT = buildSection(sections.payout);
                var isMixed = !!(payinT && payoutT);
                var legacy = payinT || payoutT || {
                    transactionBalanceFloat: 0, settlementBalanceFloat: 0, actualBalanceFloat: 0,
                    labels: null, hints: null
                };

                var totals = {
                    mode: isMixed ? 'mixed' : (payoutT ? 'payout' : 'payin'),
                    hasCrm: !!payoutT,
                    hasMixed: isMixed,
                    payin: payinT,
                    payout: payoutT,
                    transactionBalanceFloat: legacy.transactionBalanceFloat,
                    settlementBalanceFloat: legacy.settlementBalanceFloat,
                    actualBalanceFloat: legacy.actualBalanceFloat,
                    labels: legacy.labels,
                    hints: legacy.hints
                };
                setBalances({balances: projectBalances, totals: totals});
            }
            setIsLoading(false);
        }).catch(function (err) {
            Sentry.captureException(err);
            setIsLoading(false);
        });
    }, [uuid, token]);

    if (isLoading || !project) {
        return (
            React.createElement(Wrapper, null,
                React.createElement(Container, {style: {justifyContent: 'center'}},
                    React.createElement(Column, {width: '100%'},
                        React.createElement(Loader, null)
                    )
                )
            )
        );
    }

    var info = project.info || {};
    var terminals = project.terminals || [];
    var hasTotals = balances && balances.totals;
    var totals = hasTotals ? balances.totals : null;

    // Маппинг terminal_id → balanceData для сопоставления терминалов
    var balanceByTerminalId = {};
    var balanceByTerminalName = {};
    // Кто с кем делит счёт провайдера: ключ sharedBalanceGroup → список наших
    // терминалов. Нужен, чтобы подписать одинаковые суммы в таблице.
    var terminalsBySharedGroup = {};
    if (balances && balances.balances) {
        for (var i = 0; i < balances.balances.length; i++) {
            var b = balances.balances[i];
            if (b.terminal_id) {
                balanceByTerminalId[b.terminal_id] = b;
            }
            if (b.terminal_name) {
                balanceByTerminalName[b.terminal_name] = b;
            }
            if (b.sharedBalanceGroup && b.terminal_id) {
                if (!terminalsBySharedGroup[b.sharedBalanceGroup]) {
                    terminalsBySharedGroup[b.sharedBalanceGroup] = [];
                }
                terminalsBySharedGroup[b.sharedBalanceGroup].push(b.terminal_id);
            }
        }
    }

    function sharedWithFor(balanceData) {
        if (!balanceData || !balanceData.sharedBalanceGroup) return [];
        var group = terminalsBySharedGroup[balanceData.sharedBalanceGroup] || [];
        return group.filter(function (id) { return id !== balanceData.terminal_id; });
    }

    return (
        React.createElement(Wrapper, null,
            React.createElement(Container, {style: {justifyContent: 'center'}},
                React.createElement(Column, {width: '100%'},

                    // === Project Header ===
                    React.createElement("div", {className: classes.ProjectHeader},
                        React.createElement("span", {className: classes.ProjectName}, info.name),
                        React.createElement("span", {
                            className: info.status === 1 ? classes.StatusActive : classes.StatusBlocked
                        }, info.status === 1 ? "Активен" : "Заблокирован"),
                        info.url ? React.createElement(Fragment, null,
                            React.createElement("span", {className: classes.HeaderSep}),
                            React.createElement("a", {
                                href: info.url,
                                target: "_blank",
                                rel: "noopener noreferrer",
                                className: classes.HeaderUrl
                            }, info.url)
                        ) : null
                    ),

                    // === Balance Cards ===
                    hasTotals ? (function () {
                        function renderSection(section, title) {
                            var labels = (section && section.labels) || {};
                            var hints = (section && section.hints) || {};
                            var rowChildren = [
                                React.createElement("div", {key: "t", className: classes.BalanceCard, title: hints.transactionBalance || ""},
                                    React.createElement("div", {className: classes.BalanceLabel + " " + classes.TransactionLabel}, labels.transactionBalance || "Оборот"),
                                    React.createElement("div", {className: classes.BalanceValue + " " + classes.TransactionValue},
                                        formatBalance(section.transactionBalanceFloat) + " \u20BD")
                                ),
                                React.createElement("div", {key: "s", className: classes.BalanceCard, title: hints.settlementBalance || ""},
                                    React.createElement("div", {className: classes.BalanceLabel + " " + classes.SettlementLabel}, labels.settlementBalance || "Выведено"),
                                    React.createElement("div", {className: classes.BalanceValue + " " + classes.SettlementValue},
                                        formatBalance(section.settlementBalanceFloat) + " \u20BD")
                                ),
                                React.createElement("div", {key: "a", className: classes.BalanceCard, title: hints.actualBalance || ""},
                                    React.createElement("div", {className: classes.BalanceLabel + " " + classes.ActualLabel}, labels.actualBalance || "Доступно к выводу"),
                                    React.createElement("div", {className: classes.BalanceValue + " " + classes.ActualValue},
                                        formatBalance(section.actualBalanceFloat) + " \u20BD")
                                )
                            ];
                            var block = React.createElement("div", {className: classes.BalanceRow}, rowChildren);
                            if (!title) return block;
                            return React.createElement(Fragment, null,
                                React.createElement("div", {style: {fontWeight: 600, fontSize: 13, color: "#555", margin: "8px 0 4px"}}, title),
                                block
                            );
                        }
                        if (totals.hasMixed && totals.payin && totals.payout) {
                            return React.createElement(Fragment, null,
                                renderSection(totals.payin, "Приём платежей"),
                                renderSection(totals.payout, "Выплаты")
                            );
                        }
                        if (totals.payout && !totals.payin) return renderSection(totals.payout);
                        if (totals.payin && !totals.payout) return renderSection(totals.payin);
                        return renderSection(totals);
                    })() : React.createElement("div", {className: classes.BalanceRow},
                        React.createElement("div", {className: classes.BalanceCard},
                            React.createElement("div", {className: classes.BalanceLabel + " " + classes.TransactionLabel}, "Баланс"),
                            React.createElement("div", {className: classes.BalanceValue + " " + classes.TransactionValue},
                                formatBalance(project.balance) + " \u20BD"
                            )
                        )
                    ),

                    // === Project Info ===
                    React.createElement("div", {className: classes.Card},
                        React.createElement("div", {className: classes.CardHeader},
                            React.createElement("h5", {className: classes.CardTitle}, "Информация о проекте")
                        ),
                        React.createElement("div", {className: classes.CardBody},
                            React.createElement("div", {className: classes.InfoGrid},
                                React.createElement("div", {className: classes.InfoItem},
                                    React.createElement("div", {className: classes.InfoItemLabel}, "ID проекта"),
                                    React.createElement("div", {className: classes.InfoItemValue}, uuid)
                                ),
                                info.name ? React.createElement("div", {className: classes.InfoItem},
                                    React.createElement("div", {className: classes.InfoItemLabel}, "Название"),
                                    React.createElement("div", {className: classes.InfoItemValue}, info.name)
                                ) : null,
                                info.inn ? React.createElement("div", {className: classes.InfoItem},
                                    React.createElement("div", {className: classes.InfoItemLabel}, "ИНН"),
                                    React.createElement("div", {className: classes.InfoItemValue}, info.inn)
                                ) : null,
                                info.description ? React.createElement("div", {className: classes.InfoItem},
                                    React.createElement("div", {className: classes.InfoItemLabel}, "Описание"),
                                    React.createElement("div", {className: classes.InfoItemValue}, info.description)
                                ) : null,
                                info.url ? React.createElement("div", {className: classes.InfoItem},
                                    React.createElement("div", {className: classes.InfoItemLabel}, "Сайт"),
                                    React.createElement("div", {className: classes.InfoItemValue},
                                        React.createElement("a", {
                                            href: info.url,
                                            target: "_blank",
                                            rel: "noopener noreferrer",
                                            className: classes.HeaderUrl
                                        }, info.url)
                                    )
                                ) : null,
                                info.createdAt ? React.createElement("div", {className: classes.InfoItem},
                                    React.createElement("div", {className: classes.InfoItemLabel}, "Дата создания"),
                                    React.createElement("div", {className: classes.InfoItemValue}, info.createdAt)
                                ) : null
                            )
                        )
                    ),

                    // === Terminals ===
                    React.createElement("div", {className: classes.Card},
                        React.createElement("div", {className: classes.CardHeader},
                            React.createElement("h5", {className: classes.CardTitle}, "Терминалы")
                        ),
                        terminals.length > 0
                            ? React.createElement("div", {className: classes.TableWrap},
                                React.createElement("table", {className: classes.Table},
                                    React.createElement("thead", null,
                                        React.createElement("tr", null,
                                            React.createElement("th", null, "Название"),
                                            React.createElement("th", null, "Статус"),
                                            React.createElement("th", null, "Баланс"),
                                            React.createElement("th", null, "Валюта"),
                                            React.createElement("th", null)
                                        )
                                    ),
                                    React.createElement("tbody", null,
                                        terminals.map(function (t) {
                                            // Сопоставляем балансы: сначала по terminal_id, потом по имени
                                            var bd = null;
                                            if (t.terminalId && balanceByTerminalId[t.terminalId]) {
                                                bd = balanceByTerminalId[t.terminalId];
                                            } else if (t.terminalName && balanceByTerminalName[t.terminalName]) {
                                                bd = balanceByTerminalName[t.terminalName];
                                            } else if (t.name && balanceByTerminalName[t.name]) {
                                                bd = balanceByTerminalName[t.name];
                                            }
                                            return React.createElement(TerminalRow, {
                                                key: t.uuid,
                                                data: t,
                                                balanceData: bd,
                                                sharedWith: sharedWithFor(bd)
                                            });
                                        })
                                    )
                                )
                            )
                            : React.createElement("div", {className: classes.Empty}, "Нет подключённых терминалов")
                    )
                )
            )
        )
    );
});

export default ProjectSettings;



// WEBPACK FOOTER //
// ./src/PagesOfUser/ProjectSettings/index.js